/**
 * Page détail/modification d'une Matière
 *
 * - Mode lecture : affiche les détails + liste des chapitres
 * - Mode édition : formulaire inline
 * - Navigation vers chapitres
 */

import React, { useState, useCallback } from 'react';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Breadcrumbs,
  Link,
  Chip,
  CircularProgress,
  Divider,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Add, Edit, Home, Close } from '@mui/icons-material';
import { Matiere } from '../../types';
import ChapitresLeconsApiService from '../../services/chapitresLeconsApi';
import CurriculumApiService from '../../services/curriculumApi';
import MatiereForm from './MatiereForm';
import MatiereDetail from './MatiereDetail';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRBAC } from '../../contexts/RBACContext';
import { useNotify } from '../../contexts/NotificationContext';

const MatiereView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Seul un admin modifie le contenu.
  const { isAdmin } = useRBAC();
  const peutModifier = isAdmin();

  const [isEditing, setIsEditing] = useState(false);
  const { notify } = useNotify('content');
  const { environment: env } = useEnvironment();

  // Charger la matière
  const { data: matiere, isLoading: loadingMatiere } = useQuery({
    queryKey: ['matiere', id, env],
    queryFn: () => CurriculumApiService.getMatiereById(env, id!),
    enabled: !!id
  });

  // Charger la classe parente
  const { data: classe } = useQuery({
    queryKey: ['classe', matiere?.classeId, env],
    queryFn: () => CurriculumApiService.getClasseById(env, matiere!.classeId!),
    enabled: !!matiere?.classeId
  });

  // Charger les chapitres de cette matière
  const { data: allChapitres = [] } = useQuery({
    queryKey: ['chapitres', env],
    queryFn: () => ChapitresLeconsApiService.getChapitres(env)
  });

  const chapitres = allChapitres.filter(c => c.matiereId === id);

  const handleSave = useCallback(async (data: Omit<Matiere, 'id'>) => {
    if (!id) return;

    try {
      await CurriculumApiService.updateMatiere(env, id, data);
      notify('Matière mise à jour avec succès', 'success');
      setIsEditing(false);
      // Invalider le cache
      queryClient.invalidateQueries({ queryKey: ['matiere', id] });
      queryClient.invalidateQueries({ queryKey: ['matieres'] });
    } catch {
      notify('Erreur lors de la sauvegarde', 'error');
    }
  }, [id, env, queryClient, notify]);

  const handleChapitreClick = (chapitreId: string) => {
    navigate(`/chapitres/${chapitreId}`);
  };

  const columns: GridColDef[] = [
    {
      field: 'titre',
      headerName: 'Titre',
      width: 250,
      renderCell: (params) => (
        <Link
          component="button"
          variant="body2"
          onClick={() => handleChapitreClick(params.row.id)}
          sx={{ cursor: 'pointer', textAlign: 'left' }}
        >
          {params.value}
        </Link>
      )
    },
    {
      field: 'ordre',
      headerName: 'Ordre',
      width: 80
    },
    {
      field: 'niveauDifficulte',
      headerName: 'Niveau',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value || 'N/A'}
          color={params.value === 'facile' ? 'success' : params.value === 'moyen' ? 'warning' : 'error'}
          size="small"
        />
      )
    },
    {
      field: 'tempsEstimeTotalMinutes',
      headerName: 'Durée (min)',
      width: 120
    },
    {
      field: 'statut',
      headerName: 'Statut',
      width: 120,
      renderCell: (params) => {
        const statut = params.value || 'published';
        return (
          <Chip
            label={statut === 'draft' ? 'Brouillon' : 'Publié'}
            color={statut === 'draft' ? 'warning' : 'success'}
            size="small"
          />
        );
      }
    },
    {
      field: 'estActif',
      headerName: 'Actif',
      width: 80,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Oui' : 'Non'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      )
    }
  ];

  if (loadingMatiere) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!matiere) {
    return (
      <Box textAlign="center" mt={4}>
        <Typography variant="h5">Matière non trouvée</Typography>
        <Button onClick={() => navigate('/matieres')} sx={{ mt: 2 }}>
          Retour aux matières
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/')}
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
        >
          <Home sx={{ mr: 0.5 }} fontSize="small" />
          Accueil
        </Link>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/matieres')}
          sx={{ cursor: 'pointer' }}
        >
          Matières
        </Link>
        {classe && (
          <Link
            component="button"
            variant="body1"
            onClick={() => navigate(`/classes/${classe.id}`)}
            sx={{ cursor: 'pointer' }}
          >
            {classe.nom}
          </Link>
        )}
        <Typography color="text.primary">{matiere.nom}</Typography>
      </Breadcrumbs>

      {/* Informations de la matière */}
      <Paper sx={{ mb: 3, p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Typography variant="h5" component="h1">
            {matiere.nom}
          </Typography>
          {peutModifier && !isEditing && (
            <Button
              variant="contained"
              startIcon={<Edit />}
              onClick={() => setIsEditing(true)}
            >
              Modifier
            </Button>
          )}
          {isEditing && (
            <Button
              variant="outlined"
              startIcon={<Close />}
              onClick={() => setIsEditing(false)}
            >
              Annuler
            </Button>
          )}
        </Box>

        {isEditing ? (
          <MatiereForm
            matiere={matiere}
            defaultClasseId={matiere.classeId}
            onSubmit={handleSave}
            onCancel={() => setIsEditing(false)}
          />
        ) : (
          <MatiereDetail matiere={matiere} />
        )}
      </Paper>

      <Divider sx={{ my: 3 }} />

      {/* Liste des chapitres */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">
          Chapitres de cette matière ({chapitres.length})
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate(`/chapitres/new?matiereId=${id}`)}
        >
          Nouveau Chapitre
        </Button>
      </Box>

      <Paper sx={{ height: 500, width: '100%' }}>
        <DataGrid
          rows={chapitres}
          columns={columns}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          disableRowSelectionOnClick
        />
      </Paper>

    </Box>
  );
};

export default MatiereView;
